import React from "react";
import { Link } from "react-router-dom";
import { Cookie, ArrowRight } from "lucide-react";

const STORAGE_KEY = "smaastund_cookie_consent_v1";

export default function Cookies() {
  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  return (
    <div data-testid="page-cookies">
      {/* Hero */}
      <section className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#D97B66]"></span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
              Juridisk
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-[#2C302E] leading-[1.05]">
            Cookie-erklæring
          </h1>
          <p className="mt-6 text-base text-[#6B726C]">
            <strong>Sist oppdatert: 1. juni 2026</strong>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="space-y-10 text-[#2C302E]">

          <div className="flex items-start gap-4 p-6 bg-[#E5E2DC]/60 rounded-2xl">
            <Cookie className="w-6 h-6 text-[#6B726C] mt-1 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#2C302E] leading-relaxed m-0">
              Denne erklæringen forklarer hvilke informasjonskapsler (cookies) vi bruker på Småstund,
              og hvordan du kan styre dem.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Hva er cookies?</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Cookies er små tekstfiler som lagres på enheten din når du besøker en nettside. De brukes
              til å få nettsiden til å fungere bedre og gi oss innsikt i hvordan besøkende bruker siden.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Hvilke cookies bruker vi?</h2>

            <h3 className="font-heading text-xl font-medium mt-6 mb-3 text-[#2C302E]">1. Nødvendige cookies (påkrevet)</h3>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li><strong>Session cookies</strong> – holder deg innlogget under handleturen</li>
              <li><strong>CSRF-beskyttelse</strong> – sikkerhetsformål</li>
              <li><strong>Handlekurv</strong> – husker hva du har lagt i kurven</li>
            </ul>
            <p className="text-[#4A4F4A] leading-relaxed mt-3">
              Disse kan ikke slås av. De lagres bare mens du er på nettsiden.
            </p>

            <h3 className="font-heading text-xl font-medium mt-8 mb-3 text-[#2C302E]">2. Analyse-cookies (valgfritt)</h3>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>
                <strong>Google Analytics</strong> – for å forstå hvordan besøkende bruker nettsiden
                (antall besøk, hvilke sider som er populære)
              </li>
            </ul>
            <p className="text-[#4A4F4A] leading-relaxed mt-3">
              Disse kan du nekte samtykke til.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Hvordan administrere cookies?</h2>
            <p className="text-[#4A4F4A] leading-relaxed mb-3">
              Du kan når som helst endre dine cookie-innstillinger ved å:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>
                Klikke på{" "}
                <button
                  type="button"
                  onClick={reset}
                  data-testid="cookies-reset"
                  className="text-[#D97B66] underline hover:text-[#C66A55]"
                >
                  "tilbakestill cookie-valg"
                </button>{" "}
                for å se banneret igjen
              </li>
              <li>Gå til nettleserens innstillinger og slette cookies</li>
            </ul>

            <p className="text-[#4A4F4A] leading-relaxed mt-5">
              <strong>Populære nettlesere:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A] mt-2">
              <li>
                <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#D97B66] underline">
                  Chrome
                </a>
              </li>
              <li>
                <a href="https://support.mozilla.org/nb/kb/sletting-av-informasjonskapsler" target="_blank" rel="noopener noreferrer" className="text-[#D97B66] underline">
                  Firefox
                </a>
              </li>
              <li>
                <a href="https://support.apple.com/nb-no/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#D97B66] underline">
                  Safari
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Samtykke-banner</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Når du besøker nettsiden for første gang, vises en bunnbanner som ber om ditt samtykke til
              analyse-cookies. Du kan endre ditt valg når som helst.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">Kontakt oss</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Spørsmål om cookies? Send e-post til:{" "}
              <a href="mailto:post@xn--smstund-q1a.no" className="text-[#D97B66] underline">post@småstund.no</a>
            </p>
          </div>

        </div>

        {/* Back link */}
        <div className="mt-16 pt-10 border-t border-[#E5E2DC]">
          <Link
            to="/"
            data-testid="cookies-back-home"
            className="inline-flex items-center gap-2 text-[#D97B66] hover:text-[#C66A55] font-medium"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Tilbake til forsiden
          </Link>
        </div>
      </section>
    </div>
  );
}
